import { useState } from "react";
import { PRD4011_R01WorkplaceDto } from "./Dto_PRD4011_R01";

interface Props {
    data: PRD4011_R01WorkplaceDto;
    supplyIsRunning: boolean;
    dischargeIsRunning: boolean;
}

interface OperationBadgeProps {
    isRunning: boolean;
    label: string;
    positionClass: string;
}

/** 설비 이미지 (파일은 public/images/spindle 아래에 추가 필요) */
const SPINDLE_ACTIVE_IMAGE = "/images/spindle/spindle_active.png";
const SPINDLE_IDLE_IMAGE = "/images/spindle/spindle_idle.png";

const formatQty = (value?: number | null) => (value ?? 0).toLocaleString("ko-KR");

const OperationBadge = ({ isRunning, label, positionClass }: OperationBadgeProps) => (
    <div className={`absolute top-[14px] ${positionClass}`}>
        <span
            className={`inline-flex items-center gap-[8px] px-[20px] py-[6px] rounded-full text-white font-extrabold ${
                isRunning ? "bg-[#14632f]" : "bg-[#374151]"
            }`}
        >
            {isRunning ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 4v16l13-8z" />
                </svg>
            ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                </svg>
            )}
            <span className="flex flex-col items-center leading-none">
                <span className="text-[20px]">{isRunning ? "동작 중" : "정지"}</span>
                <span className="mt-[3px] text-[11px] font-bold text-white/80">{label}</span>
            </span>
        </span>
    </div>
);

export const LineCard_PRD4011_R01 = ({
    data,
    supplyIsRunning,
    dischargeIsRunning,
}: Props) => {
    const isRunning = supplyIsRunning || dischargeIsRunning;
    const statusImage = isRunning ? SPINDLE_ACTIVE_IMAGE : SPINDLE_IDLE_IMAGE;
    const [failedImage, setFailedImage] = useState<string | null>(null);
    const isImageFailed = failedImage === statusImage;

    return (
        <div className="flex flex-col bg-white border border-[#d7dde8] rounded-lg overflow-hidden">
            {/* 헤더 */}
            <div className="flex items-center justify-between px-[22px] py-[14px] bg-[#2f56a8] text-white">
                <span className="text-[28px] font-extrabold">{data.workplaceNm}</span>
                <span className="inline-flex items-center gap-[6px] text-[15px] text-[#dbe5f7] tabular-nums">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="5" width="18" height="16" rx="2" />
                        <path d="M3 10h18" />
                        <path d="M8 3v4" />
                        <path d="M16 3v4" />
                    </svg>
                    {data.lastDataTime ?? "-"}
                </span>
            </div>

            {/* 설비 이미지 + 동작 상태 배지 */}
            <div className="relative h-[250px] flex items-center justify-center bg-[#dfe4ec] text-[#5a6577]">
                {isImageFailed ? (
                    <span className="text-[16px]">
                        [설비 이미지 — {isRunning ? "동작" : "정지"}]
                    </span>
                ) : (
                    <img
                        src={statusImage}
                        alt={data.workplaceNm}
                        className="w-full h-full object-cover"
                        onError={() => setFailedImage(statusImage)}
                    />
                )}
                <OperationBadge
                    isRunning={supplyIsRunning}
                    label="공급부"
                    positionClass="left-[14px]"
                />
                <OperationBadge
                    isRunning={dischargeIsRunning}
                    label="배출부"
                    positionClass="right-[14px]"
                />
            </div>

            {/* 생산량 */}
            <div className="flex flex-col gap-[10px] px-[20px] py-[16px]">
                <div className="h-[76px] flex items-center border border-[#dbe8f3] rounded-lg bg-[#f7faff]">
                    <div className="w-[290px] h-[52px] pl-[20px] flex items-center gap-[16px] border-r border-[#d6e3ee] text-[24px] font-extrabold text-[#19365f]">
                        <span className="w-[48px] h-[48px] inline-flex items-center justify-center rounded-[10px] bg-[#e3eefc] text-[#1d6fd6]">
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 20v-4" />
                                <path d="M9 20v-7" />
                                <path d="M14 20v-5" />
                                <path d="M19 20V10" />
                                <path d="M3 11l6-5 4 3 8-6" />
                                <path d="M16 3h5v5" />
                            </svg>
                        </span>
                        금일생산량
                    </div>
                    <div className="flex-1 h-[56px] mx-[14px] flex items-center justify-center gap-[10px] rounded-[10px] bg-[#e8f1fc]">
                        <span className="text-[50px] font-extrabold leading-none text-[#1764c0] tabular-nums">
                            {formatQty(data.dayQty)}
                        </span>
                        <span className="text-[20px] font-bold text-[#5a6f8c]">EA</span>
                    </div>
                </div>

                <div className="h-[76px] flex items-center border border-[#dbe8f3] rounded-lg bg-[#f7faff]">
                    <div className="w-[290px] h-[52px] pl-[20px] flex items-center gap-[16px] border-r border-[#d6e3ee] text-[24px] font-extrabold text-[#19365f]">
                        <span className="w-[48px] h-[48px] inline-flex items-center justify-center rounded-[10px] bg-[#e3eefc] text-[#1d6fd6]">
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2 3 7v10l9 5 9-5V7l-9-5z" />
                                <path d="M3 7l9 5 9-5" />
                                <path d="M12 12v10" />
                            </svg>
                        </span>
                        현재Lot 생산량
                    </div>
                    <div className="flex-1 h-[56px] mx-[14px] flex items-center justify-center gap-[10px] rounded-[10px] bg-[#e8f1fc]">
                        <span className="text-[50px] font-extrabold leading-none text-[#1764c0] tabular-nums">
                            {formatQty(data.lotQty)}
                        </span>
                        <span className="text-[20px] font-bold text-[#5a6f8c]">EA</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
